import CreativeFileUpload from './CreativeFileUpload';
import AdvancedDocumentEditor from './AdvancedDocumentEditor';
import React, { useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { getUsersNameEmailByCompany } from 'services/users-service';
import { useAuthTenant } from 'store/auth';
import PolicyEditor from './Editor';
import { useTranslation } from 'react-i18next';
import { Dialog, Select, TextField } from 'Components/ui';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

/* ── Styled Components ── */

const FormWrap = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: ${p => p.$mb || '24px'};
  padding-bottom: 16px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const IconBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${p => p.$gradient || 'linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

const SectionSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${p => p.$full && 'grid-column: 1 / -1;'}
`;

const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`;

const ErrorText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-danger);
  margin: 0;
`;

const EditorWrap = styled.div`
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const UploadZone = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1.5px dashed var(--vo-line);
  border-radius: 10px;
  cursor: pointer;
  &:hover { background: var(--vo-capri-25); }
`;

const UploadLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink-2);
  margin: 0 0 2px;
`;

const UploadHelper = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

/* Attached document (edit mode) */
const AttachedCard = styled.div`
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  padding: 20px;
  background: var(--vo-capri-25);
`;

const AttachedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const AttachedName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

const AttachedMeta = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 4px 0 0;
`;

const SwapBtn = styled.button`
  height: 36px;
  background: white;
  color: var(--vo-celeste);
  border: 1.5px solid var(--vo-celeste);
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover:not(:disabled) { background: var(--vo-capri-50); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const SwapActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const SecondaryBtn = styled.button`
  height: 36px;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--vo-mist); }
`;

const PrimaryBtn = styled.button`
  height: 36px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: var(--vo-cobalto); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

/* ── SVG Icons ── */
const IconDoc = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);

const IconLines = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="17" y1="10" x2="3" y2="10"/>
    <line x1="21" y1="6" x2="3" y2="6"/>
    <line x1="21" y1="14" x2="3" y2="14"/>
    <line x1="17" y1="18" x2="3" y2="18"/>
  </svg>
);

const IconUpload = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--vo-slate)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

/* ── Status Options ── */
const getStatusOptions = (t) => [
  { value: 'Iniciado',  label: t('policy:versionForm.status.started') },
  { value: 'Concluído', label: t('policy:versionForm.status.completed') },
];

/* ── Component ── */

export const VersionFormComponent = ({
  control,
  errors,
  getValues,
  handleComplete,
  handleUpdateAttachment,
  handleSubmit,
  register,
  setValue,
  existingData,
  policyName,
}) => {
  const { t } = useTranslation(['policy']);
  const selectedTenant = useSelector(useAuthTenant);

  const submittingRef = useRef(false);
  const [isChangingDocument, setIsChangingDocument] = useState(false);
  const [newDocument, setNewDocument] = useState(null);
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);

  const isEditMode = !!(existingData && existingData.attachment);

  const { data: users } = useQuery(['getUsers'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getUsersNameEmailByCompany(selectedTenant.slug);
    return result?.data?.length > 0 ? result.data : [];
  });

  const pickId = (v) => (typeof v === 'string' ? v : (v?._id || v?.id || ''));

  const handleSaveAdvancedEdit = (newContent) => {
    setValue('policyText', newContent);
    Dialog.alert({
      title: t('policy:versionForm.swal.successTitle'),
      message: t('policy:versionForm.swal.saveSuccess'),
      icon: 'success',
      confirmLabel: 'OK',
    });
  };

  const handleChangeDocument = () => setIsChangingDocument(true);
  const handleCancelChangeDocument = () => { setIsChangingDocument(false); setNewDocument(null); };

  const handleConfirmNewDocument = async () => {
    if (!newDocument) {
      Dialog.alert({ title: t('policy:versionForm.swal.noFileTitle'), message: t('policy:versionForm.swal.noFileText'), icon: 'warning', confirmLabel: 'OK' });
      return;
    }
    const confirmed = await Dialog.confirm({
      title: t('policy:versionForm.swal.confirmSwapTitle'),
      message: t('policy:versionForm.swal.confirmSwapText'),
      confirmLabel: t('policy:versionForm.swal.confirmSwapYes'),
      cancelLabel: t('policy:versionForm.swal.confirmSwapCancel'),
    });
    if (!confirmed) return;
    try {
      Dialog.loading({ title: t('policy:versionForm.swal.loadingTitle'), message: t('policy:versionForm.swal.loadingText') });
      if (existingData && handleUpdateAttachment) {
        await handleUpdateAttachment(newDocument);
      } else {
        const form = getValues();
        await handleComplete?.({
          version: String(form.version || '').trim(),
          status: form.status,
          companyKey: selectedTenant?.slug || form.companyKey || '',
          authorId: pickId(form.author),
          reviewerId: pickId(form.reviewer),
          approverId: pickId(form.approver),
          policyText: form.policyText || existingData?.policyText || '',
          attachment: newDocument,
        });
      }
      Dialog.alert({ title: t('policy:versionForm.swal.successTitle'), message: t('policy:versionForm.swal.swapSuccess'), icon: 'success', confirmLabel: 'OK' })
        .then(() => { setIsChangingDocument(false); setNewDocument(null); window.location.reload(); });
    } catch (error) {
      console.error('Erro ao trocar documento:', error);
      Dialog.alert({ title: t('policy:versionForm.swal.errorTitle'), message: t('policy:versionForm.swal.swapError'), icon: 'error', confirmLabel: 'OK' });
    }
  };

  const onSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const form = getValues();
      const normalizedPayload = {
        version: String(form.version || '').trim(),
        status: form.status,
        companyKey: selectedTenant?.slug || form.companyKey || '',
        authorId: pickId(form.author),
        reviewerId: pickId(form.reviewer),
        approverId: pickId(form.approver),
        policyText: isEditMode ? (existingData.policyText || '') : (form.policyText || ''),
        attachment: isEditMode
          ? (form.attachment || existingData.attachment)
          : (form.attachment || null),
      };
      await handleComplete?.(normalizedPayload);
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      submittingRef.current = false;
    }
  };

  const statusOptions = getStatusOptions(t);

  return (
    <div
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
          e.preventDefault();
        }
      }}
    >
      <form id="version-form" onSubmit={handleSubmit(onSubmit)}>
        <FormWrap>

          {/* ── Metadados Card ── */}
          <FormCard>
            <CardHeader>
              <IconBox $gradient="linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))">
                <IconDoc />
              </IconBox>
              <div>
                <SectionTitle>{t('policy:versionForm.sectionTitle')}</SectionTitle>
                <SectionSubtitle>{policyName || '—'}</SectionSubtitle>
              </div>
            </CardHeader>

            <FieldsGrid>
              {/* Version – full width */}
              <Field $full>
                <FieldLabel htmlFor="version-number">
                  {t('policy:versionForm.version.label')} *
                </FieldLabel>
                <TextField
                  fullWidth
                  id="version-number"
                  autoFocus
                  error={!!errors?.version}
                  {...register('version', { required: t('policy:versionForm.version.required') })}
                />
                {errors?.version && <ErrorText>{errors.version.message}</ErrorText>}
              </Field>

              {/* Status */}
              <Field>
                <FieldLabel htmlFor="version-status">
                  {t('policy:versionForm.status.label')} *
                </FieldLabel>
                <Controller
                  control={control}
                  name="status"
                  rules={{ required: t('policy:versionForm.status.required') }}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <>
                      <Select
                        name="status"
                        error={!!fieldState.error}
                        value={value ?? ''}
                        onChange={onChange}
                        options={[
                          { value: '', label: t('policy:versionForm.status.placeholder') },
                          ...statusOptions,
                        ]}
                      />
                      {fieldState.error && <ErrorText>{fieldState.error.message}</ErrorText>}
                    </>
                  )}
                />
              </Field>

              {/* Author */}
              <Field>
                <FieldLabel htmlFor="version-author">
                  {t('policy:versionForm.author.label')} *
                </FieldLabel>
                <Controller
                  control={control}
                  name="author"
                  rules={{ required: t('policy:versionForm.author.required') }}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <>
                      <Select
                        id="version-author"
                        name="author"
                        value={value}
                        onChange={onChange}
                        error={!!fieldState.error}
                        options={[
                          { value: '', label: t('policy:versionForm.author.placeholder') },
                          ...(users || []).map((u) => ({ value: u, label: u.name })),
                        ]}
                      />
                      {fieldState.error && <ErrorText>{fieldState.error.message}</ErrorText>}
                    </>
                  )}
                />
              </Field>

              {/* Reviewer */}
              <Field>
                <FieldLabel htmlFor="version-reviewer">
                  {t('policy:versionForm.reviewer.label')} *
                </FieldLabel>
                <Controller
                  control={control}
                  name="reviewer"
                  rules={{ required: t('policy:versionForm.reviewer.required') }}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <>
                      <Select
                        id="version-reviewer"
                        name="reviewer"
                        value={value}
                        onChange={onChange}
                        error={!!fieldState.error}
                        options={[
                          { value: '', label: t('policy:versionForm.reviewer.placeholder') },
                          ...(users || []).map((u) => ({ value: u, label: u.name })),
                        ]}
                      />
                      {fieldState.error && <ErrorText>{fieldState.error.message}</ErrorText>}
                    </>
                  )}
                />
              </Field>

              {/* Approver */}
              <Field>
                <FieldLabel htmlFor="version-approver">
                  {t('policy:versionForm.approver.label')} *
                </FieldLabel>
                <Controller
                  control={control}
                  name="approver"
                  rules={{ required: t('policy:versionForm.approver.required') }}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <>
                      <Select
                        id="version-approver"
                        name="approver"
                        value={value}
                        onChange={onChange}
                        error={!!fieldState.error}
                        options={[
                          { value: '', label: t('policy:versionForm.approver.placeholder') },
                          ...(users || []).map((u) => ({ value: u, label: u.name })),
                        ]}
                      />
                      {fieldState.error && <ErrorText>{fieldState.error.message}</ErrorText>}
                    </>
                  )}
                />
              </Field>
            </FieldsGrid>
          </FormCard>

          {/* ── Conteúdo Card (new / text-only edit) ── */}
          {!isEditMode && (
            <FormCard>
              <CardHeader $mb="20px">
                <IconBox $gradient="linear-gradient(135deg, var(--vo-purple), color-mix(in srgb, var(--vo-purple) 60%, white))">
                  <IconLines />
                </IconBox>
                <div>
                  <SectionTitle>{t('policy:versionForm.contentTitle')}</SectionTitle>
                  <SectionSubtitle>{t('policy:versionForm.contentSubtitle')}</SectionSubtitle>
                </div>
              </CardHeader>

              {/* Rich text editor */}
              <Controller
                control={control}
                name="policyText"
                render={({ field: { onChange, value } }) => (
                  <EditorWrap>
                    <PolicyEditor value={value} onChange={onChange} />
                  </EditorWrap>
                )}
              />

              {/* File upload zone */}
              <Controller
                control={control}
                name="attachment"
                render={({ field: { onChange, value } }) => (
                  value ? (
                    /* File already selected — delegate to CreativeFileUpload for preview/remove */
                    <CreativeFileUpload
                      id="attachment-upload"
                      value={value}
                      onChange={onChange}
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    />
                  ) : (
                    /* Dashed zone — click to open file picker */
                    <>
                      <UploadZone htmlFor="attachment-file-input">
                        <IconUpload />
                        <div>
                          <UploadLabel>{t('policy:versionForm.attachLabel')}</UploadLabel>
                          <UploadHelper>{t('policy:versionForm.attachHelper')}</UploadHelper>
                        </div>
                      </UploadZone>
                      <HiddenInput
                        id="attachment-file-input"
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) onChange(file);
                          e.target.value = '';
                        }}
                      />
                    </>
                  )
                )}
              />
            </FormCard>
          )}

          {/* ── Attached Document Card (edit mode with existing attachment) ── */}
          {isEditMode && (
            <FormCard>
              <CardHeader $mb="20px">
                <IconBox $gradient="linear-gradient(135deg, var(--vo-purple), color-mix(in srgb, var(--vo-purple) 60%, white))">
                  <IconLines />
                </IconBox>
                <div>
                  <SectionTitle>{t('policy:versionForm.contentTitle')}</SectionTitle>
                  <SectionSubtitle>{t('policy:versionForm.contentSubtitle')}</SectionSubtitle>
                </div>
              </CardHeader>

              <AttachedCard>
                <AttachedHeader>
                  <div>
                    <AttachedName>
                      {t('policy:versionForm.attachedDoc.title', { name: existingData.attachment.originalName })}
                    </AttachedName>
                    <AttachedMeta>
                      {existingData.attachment.mimetype} • {Math.round(existingData.attachment.size / 1024)} KB
                    </AttachedMeta>
                  </div>
                  <SwapBtn
                    type="button"
                    onClick={handleChangeDocument}
                    disabled={isChangingDocument}
                  >
                    {t('policy:versionForm.attachedDoc.swap')}
                  </SwapBtn>
                </AttachedHeader>

                {isChangingDocument && (
                  <>
                    <CreativeFileUpload
                      id="new-document-upload"
                      value={newDocument}
                      onChange={setNewDocument}
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      helperText={t('policy:versionForm.attachment.helper')}
                    />
                    <SwapActions>
                      <SecondaryBtn type="button" onClick={handleCancelChangeDocument}>
                        {t('policy:versionForm.attachedDoc.cancel')}
                      </SecondaryBtn>
                      <PrimaryBtn
                        type="button"
                        onClick={handleConfirmNewDocument}
                        disabled={!newDocument}
                      >
                        {t('policy:versionForm.attachedDoc.confirmSwap')}
                      </PrimaryBtn>
                    </SwapActions>
                  </>
                )}
              </AttachedCard>
            </FormCard>
          )}

        </FormWrap>
      </form>

      {/* Advanced document editor modal */}
      {showAdvancedEditor && (
        <AdvancedDocumentEditor
          value={getValues('policyText') || ''}
          onChange={handleSaveAdvancedEdit}
          onClose={() => setShowAdvancedEditor(false)}
          pdfUrl={existingData?.attachment?.url}
          title={`Editor Avançado — ${existingData?.attachment?.originalName || 'Documento'}`}
        />
      )}
    </div>
  );
};
